import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy, ShieldAlert, Check, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createHelpRequest, RequestKind } from '../../services/helpRequestService';

interface HelpButtonProps {
  leftOffset?: number;
}

type State = 'idle' | 'sending' | 'sent';

interface FloatingRequestButtonProps {
  kind: RequestKind;
  title: string;
  sentLabel: string;
  idleColors: string;
  sentColors: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const FloatingRequestButton: React.FC<FloatingRequestButtonProps> = ({
  kind,
  title,
  sentLabel,
  idleColors,
  sentColors,
  Icon,
}) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [state, setState] = useState<State>('idle');

  const send = async () => {
    if (state !== 'idle' || !currentUser) return;
    setState('sending');
    try {
      await createHelpRequest({
        kind,
        requesterId: currentUser.uid,
        requesterName: currentUser.displayName || currentUser.email || 'Someone',
        requesterEmail: currentUser.email || '',
        page: location.pathname,
      });
      setState('sent');
      setTimeout(() => setState('idle'), 6000);
    } catch (err) {
      console.error(`Failed to send ${kind} request`, err);
      setState('idle');
    }
  };

  const sent = state === 'sent';
  const sending = state === 'sending';

  return (
    <div className="relative flex items-center">
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        onClick={send}
        disabled={sending}
        title={sent ? sentLabel : title}
        aria-label={sent ? sentLabel : title}
        className={`p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 ${
          sent ? sentColors : idleColors
        }`}
      >
        {sending ? (
          <Loader2 className="h-5 w-5 text-white animate-spin" />
        ) : sent ? (
          <Check className="h-5 w-5 text-white" />
        ) : (
          <Icon className="h-5 w-5 text-white" />
        )}
      </motion.button>

      <AnimatePresence>
        {sent && (
          <motion.span
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -6 }}
            className="ml-2 whitespace-nowrap rounded-lg bg-gray-900 px-2.5 py-1 text-xs font-medium text-white shadow-lg"
          >
            {sentLabel}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Icon-only floating buttons shown (in place of the metal-prices bar) for
 * store-only users: a green Help button and a red Security button. Each pings
 * its designated responders, who get an on-screen popup + ding while they have
 * the app open.
 */
const HelpButton: React.FC<HelpButtonProps> = ({ leftOffset = 16 }) => {
  return (
    <div className="fixed bottom-4 z-50 flex flex-col gap-2" style={{ left: leftOffset }}>
      <FloatingRequestButton
        kind="security"
        title="Request security"
        sentLabel="Security alerted"
        idleColors="bg-gradient-to-br from-rose-500 to-red-600"
        sentColors="bg-gradient-to-br from-emerald-500 to-green-600"
        Icon={ShieldAlert}
      />
      <FloatingRequestButton
        kind="help"
        title="Request help"
        sentLabel="Help is on the way"
        idleColors="bg-gradient-to-br from-emerald-500 to-green-600"
        sentColors="bg-gradient-to-br from-emerald-600 to-green-700"
        Icon={LifeBuoy}
      />
    </div>
  );
};

export default HelpButton;
